import { ServicesManager, Types } from '@ohif/core';

import {
  ContextMenuController,
  defaultContextMenu,
} from './CustomizeableContextMenu';
import DicomTagBrowser from './DicomTagBrowser/DicomTagBrowser';
import reuseCachedLayouts from './utils/reuseCachedLayouts';
import findViewportsByPosition, {
  findOrCreateViewport as layoutFindOrCreate,
} from './findViewportsByPosition';
import React from 'react';
import axios from 'axios';
import moment from 'moment';
import { ImagemService } from './Service/ImagemService';
import html2canvas from 'html2canvas';
import { ContextMenuProps } from './CustomizeableContextMenu/types';
import ImagemSecundariaForm from './../../../extensions/cornerstone/src/utils/ImagemSecundariaForm';
import {
  segmentation,
  SegmentationDisplayTool,
  ToolGroupManager,
  Enums
} from '@cornerstonejs/tools';
import extension from '../../cornerstone-dicom-seg/src';
import { metaData } from '@cornerstonejs/core';

export type HangingProtocolParams = {
  protocolId?: string;
  stageIndex?: number;
  activeStudyUID?: string;
  stageId?: string;
};

/**
 * Determine if a command is a hanging protocol one.
 * For now, just use the two hanging protocol commands that are in this
 * commands module, but if others get added elsewhere this may need enhancing.
 */
const isHangingProtocolCommand = command =>
  command &&
  (command.commandName === 'setHangingProtocol' ||
    command.commandName === 'toggleHangingProtocol');

const commandsModule = ({
  servicesManager,
  commandsManager,
}: Types.Extensions.ExtensionParams): Types.Extensions.CommandsModule => {
  const {
    customizationService,
    measurementService,
    hangingProtocolService,
    uiNotificationService,
    viewportGridService,
    displaySetService,
    stateSyncService,
    toolbarService,
    cornerstoneViewportService
  } = (servicesManager as ServicesManager).services;

  // Define a context menu controller for use with any context menus
  const contextMenuController = new ContextMenuController(
    servicesManager,
    commandsManager
  );

  let attempts = 0;
  const maxAttempts = 10;

  async function aguardarCarregamentoDasImagens(displayset, totalInstancias, timeoutMs = 150000) {
    return new Promise((resolve, reject) => {
      const startTime = Date.now(); // Marca o início da execução

      function verificarCarregamento() {
        var totalInstanciasCarregadas = displayset.images.length;

        var loadingText = document.getElementById("loading-text");
        if (loadingText) {
          loadingText.textContent = `Carregando imagens: ${totalInstanciasCarregadas} / ${totalInstancias}`;
        }

        if (totalInstanciasCarregadas >= totalInstancias) {
          if (loadingText) {
            loadingText.textContent = "Renderizando...";
          }
          setTimeout(() => {
            resolve();
          }, 1000);
        } else if (Date.now() - startTime >= timeoutMs) {
          // Se o tempo máximo for atingido, rejeita a Promise
          reject(new Error("Falha ao renderizar MPR"));
        } else {
          setTimeout(verificarCarregamento, 500);
        }
      }

      verificarCarregamento();
    });
  }


  function showLoading() {
    const loadingDiv = document.createElement("div");
    loadingDiv.id = "custom-loading";
    loadingDiv.style.position = "fixed";
    loadingDiv.style.top = "0";
    loadingDiv.style.left = "0";
    loadingDiv.style.width = "100vw";
    loadingDiv.style.height = "100vh";
    loadingDiv.style.background = "rgba(0, 0, 0, 0.5)";
    loadingDiv.style.display = "flex";
    loadingDiv.style.flexDirection = "column";
    loadingDiv.style.alignItems = "center";
    loadingDiv.style.justifyContent = "center";
    loadingDiv.style.zIndex = "9999";

    // Criando o spinner
    const spinner = document.createElement("div");
    spinner.style.position = "relative";
    spinner.style.width = "50px";
    spinner.style.height = "50px";
    spinner.style.border = "5px solid rgb(90, 204, 230)"; // Cor da borda do spinner (verde)
    spinner.style.borderTop = "5px solid transparent";
    spinner.style.borderRadius = "50%";
    spinner.style.animation = "spin 1s linear infinite";

    // Criando o texto de carregamento
    const loadingText = document.createElement("p");
    loadingText.id = "loading-text";
    loadingText.style.color = "white";
    loadingText.style.marginTop = "15px";
    loadingText.style.fontSize = "18px";
    loadingText.style.fontFamily = "Arial, sans-serif";
    loadingText.textContent = "Carregando imagens: 0 / 0"; // Começa com zero

    loadingDiv.appendChild(spinner);
    loadingDiv.appendChild(loadingText); // Adicionando o texto abaixo do spinner
    document.body.appendChild(loadingDiv);
  }

  function hideLoading() {
    const loadingDiv = document.getElementById("custom-loading");
    if (loadingDiv) {
      document.body.removeChild(loadingDiv);
    }
  }

  function hideLoadingSuccess(displayuid) {
    var displayset = window.services.displaySetService.activeDisplaySets.find(
      ds => ds.displaySetInstanceUID === displayuid
    );
    if (!displayset) {
      return;
    }
    var interval = setInterval(() => {
      if (displayset.mprLoaded) {
        clearInterval(interval)
        const loadingDiv = document.getElementById("custom-loading");
        if (loadingDiv) {
          document.body.removeChild(loadingDiv);
          //Quando loading fechar, também clico no botão de habilitar as marcações do MPR manualmente.
          document.getElementById("icon-Crosshairs")?.click();
          attempts = 0;
        }
      }
    })
  }
  const style = document.createElement("style");
  style.innerHTML = `
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `;
  document.head.appendChild(style);

  const actions = {
    /**
     * Show the context menu.
     * @param options.menuId defines the menu name to lookup, from customizationService
     * @param options.defaultMenu contains the default menu set to use
     * @param options.element is the element to show the menu within
     * @param options.event is the event that caused the context menu
     * @param options.selectorProps is the set of selection properties to use
     */
    showContextMenu: (options: ContextMenuProps) => {
      const {
        menuCustomizationId,
        element,
        event,
        selectorProps,
        defaultPointsPosition = [],
      } = options;

      const optionsToUse = { ...options };

      if (menuCustomizationId) {
        Object.assign(
          optionsToUse,
          customizationService.get(menuCustomizationId, defaultContextMenu)
        );
      }

      // TODO - make the selectorProps richer by including the study metadata and display set.
      const { protocol, stage } = hangingProtocolService.getActiveProtocol();
      optionsToUse.selectorProps = {
        event,
        protocol,
        stage,
        ...selectorProps,
      };

      contextMenuController.showContextMenu(
        optionsToUse,
        element,
        defaultPointsPosition
      );
    },

    /** Close a context menu currently displayed */
    closeContextMenu: () => {
      contextMenuController.closeContextMenu();
    },

    displayNotification: ({ text, title, type }) => {
      uiNotificationService.show({
        title: title,
        message: text,
        type: type,
      });
    },
    clearMeasurements: () => {
      measurementService.clear();
    },

    /**
     * Toggles off all tools which contain a commandName of setHangingProtocol
     * or toggleHangingProtocol, and which match/don't match the protocol id/stage
     */
    toggleHpTools: () => {
      const {
        protocol,
        stageIndex: toggleStageIndex,
        stage,
      } = hangingProtocolService.getActiveProtocol();
      const enableListener = button => {
        if (!button.id) return;
        const { commands, items } = button.props || button;
        if (items) {
          items.forEach(enableListener);
        }
        const hpCommand = commands?.find?.(isHangingProtocolCommand);
        if (!hpCommand) return;
        const { protocolId, stageIndex, stageId } = hpCommand.commandOptions;
        const isActive =
          (!protocolId || protocolId === protocol.id) &&
          (stageIndex === undefined || stageIndex === toggleStageIndex) &&
          (!stageId || stageId === stage.id);
        toolbarService.setActive(button.id, isActive);
      };
      Object.values(toolbarService.getButtons()).forEach(enableListener);
    },

    /**
     *  Sets the specified protocol
     *    1. Records any existing state using the viewport grid service
     *    2. Finds the destination state - this can be one of:
     *       a. The specified protocol stage
     *       b. An alternate (toggled or restored) protocol stage
     *       c. A restored custom layout
     *    3. Finds the parameters for the specified state
     *       a. Gets the displaySetSelectorMap
     *       b. Gets the map by position
     *       c. Gets any toggle mapping to map position to/from current view
     *    4. If restore, then sets layout
     *       a. Maps viewport position by currently displayed viewport map id
     *       b. Uses toggle information to map display set id
     *    5. Else applies the hanging protocol
     *       a. HP Service is provided displaySetSelectorMap
     *       b. HP Service will throw an exception if it isn't applicable
     * @param options - contains information on the HP to apply
     * @param options.activeStudyUID - the updated study to apply the HP to
     * @param options.protocolId - the protocol ID to change to
     * @param options.stageId - the stageId to apply
     * @param options.stageIndex - the index of the stage to go to.
     */
    setHangingProtocol: async ({
      activeStudyUID = '',
      protocolId,
      stageId,
      stageIndex,
    }: HangingProtocolParams): Promise<boolean> => {
      try {
        // Stores in the state the reuseID to displaySetUID mapping
        // Pass in viewportId for the active viewport.  This item will get set as
        // the activeViewportId


        /*IMPLEMENTADO*/
        //Aguardo todas as imagens da série baixar.
        const viewportsInfo = cornerstoneViewportService.viewportsInfo.get(0);

        //Exibo loading.
        var loading = document.getElementById("custom-loading")
        if (!loading && viewportsInfo.viewportOptions.viewportType !== 'orthographic') {
          showLoading();
        }

        if (viewportsInfo?.viewportData?.data?.displaySetInstanceUID) {
          var displayuid = viewportsInfo.viewportData.data.displaySetInstanceUID;

          var displayset = window.services.displaySetService.activeDisplaySets.find(
            ds => ds.displaySetInstanceUID === displayuid
          );

          var totalInstancias = window.GblVar.SeriesCarregadas.find(
            ser => ser.Serie === displayset.SeriesInstanceUID
          ).Number;

          await aguardarCarregamentoDasImagens(displayset, totalInstancias);
        }

        const state = viewportGridService.getState();
        const hpInfo = hangingProtocolService.getState();
        const {
          protocol: oldProtocol,
        } = hangingProtocolService.getActiveProtocol();
        const stateSyncReduce = reuseCachedLayouts(
          state,
          hangingProtocolService,
          stateSyncService
        );
        const {
          hangingProtocolStageIndexMap,
          viewportGridStore,
          displaySetSelectorMap,
        } = stateSyncReduce;

        if (!protocolId) {
          // Re-use the previous protocol id, and optionally stage
          protocolId = hpInfo.protocolId;
          if (stageId === undefined && stageIndex === undefined) {
            stageIndex = hpInfo.stageIndex;
          }
        } else if (stageIndex === undefined && stageId === undefined) {
          // Re-set the same stage as was previously used
          const hangingId = `${activeStudyUID ||
            hpInfo.activeStudyUID}:${protocolId}`;
          stageIndex = hangingProtocolStageIndexMap[hangingId]?.stageIndex;
        }

        const useStageIdx =
          stageIndex ??
          hangingProtocolService.getStageIndex(protocolId, {
            stageId,
            stageIndex,
          });

        if (activeStudyUID) {
          hangingProtocolService.setActiveStudyUID(activeStudyUID);
        }

        const storedHanging = `${hangingProtocolService.getState().activeStudyUID
          }:${protocolId}:${useStageIdx || 0}`;

        const restoreProtocol = !!viewportGridStore[storedHanging];

        if (
          protocolId === hpInfo.protocolId &&
          useStageIdx === hpInfo.stageIndex &&
          !activeStudyUID
        ) {
          // Clear the HP setting to reset them
          hangingProtocolService.setProtocol(protocolId, {
            stageId,
            stageIndex: useStageIdx,
          });
        } else {
          hangingProtocolService.setProtocol(protocolId, {
            displaySetSelectorMap,
            stageId,
            stageIndex: useStageIdx,
            restoreProtocol,
          });
          if (restoreProtocol) {
            viewportGridService.set(viewportGridStore[storedHanging]);
          }
        }
        // Do this after successfully applying the update
        stateSyncService.store(stateSyncReduce);
        // This is a default action applied
        actions.toggleHpTools(hangingProtocolService.getActiveProtocol());
        // Send the notification about updating the state
        if (protocolId !== hpInfo.protocolId) {
          const { protocol } = hangingProtocolService.getActiveProtocol();
          // The old protocol callbacks are used for turning off things
          // like crosshairs when moving to the new HP
          commandsManager.run(oldProtocol.callbacks?.onProtocolExit);
          // The new protocol callback is used for things like
          // activating modes etc.
          commandsManager.run(protocol.callbacks?.onProtocolEnter);
        }

        if (attempts > maxAttempts) {
          setTimeout(() => {
            hideLoading();
            uiNotificationService.show({
              title: 'MPR',
              message: 'Falha ao renderizar MPR.',
              type: 'error',
              duration: 10000,
            });
            attempts = 0;
            return false;
          }, 5000);
        }

        //Valido se todas as imagens estão rederizadas, se não estiver, eu clico manualmente no botão duas vezes para abrir e fechar MPR (Gambiarra).
        var pending = 0;
        if (displayset) {
          displayset.images.forEach(image => {
            var instance = metaData.get('imagePlaneModule', image.url); //Se ele não achar a instancia, quer dizer que a imagem ainda não foi renderizada.
            if (instance === undefined) {
              pending = pending + 1;
            }
          });
        }
        if (pending !== 0) {
          attempts++;
          setTimeout(() => {
            document.getElementById("icon-MPR")?.click();
            setTimeout(() => {
              document.getElementById("icon-MPR")?.click();
            }, 1000);
          }, 1000);
        } else {
          hideLoadingSuccess(displayuid);
        }

        return true;
      } catch (e) {
        /*IMPLEMENTADO*/
        hideLoading();
        attempts = 0;
        var message = 'A série não contém imagens 3D!'
        var type = 'error';
        if (e.message.includes('Falha ao renderizar MPR')) {
          message = 'Falha ao montar MPR.';
          type = 'error';
        }
        actions.toggleHpTools(hangingProtocolService.getActiveProtocol());
        uiNotificationService.show({
          title: 'MPR',
          message: message,
          type: type,
          duration: 3000,
        });
        return false;
      }
    },

    toggleHangingProtocol: ({
      protocolId,
      stageIndex,
    }: HangingProtocolParams): boolean => {
      const {
        protocol,
        stageIndex: desiredStageIndex,
        activeStudy,
      } = hangingProtocolService.getActiveProtocol();
      const { toggleHangingProtocol } = stateSyncService.getState();
      const storedHanging = `${activeStudy.StudyInstanceUID
        }:${protocolId}:${stageIndex | 0}`;
      if (
        protocol.id === protocolId &&
        (stageIndex === undefined || stageIndex === desiredStageIndex)
      ) {
        // Toggling off - restore to previous state
        const previousState = toggleHangingProtocol[storedHanging] || {
          protocolId: 'default',
        };
        return actions.setHangingProtocol(previousState);
      } else {
        stateSyncService.store({
          toggleHangingProtocol: {
            ...toggleHangingProtocol,
            [storedHanging]: {
              protocolId: protocol.id,
              stageIndex: desiredStageIndex,
            },
          },
        });
        return actions.setHangingProtocol({ protocolId, stageIndex });
      }
    },

    deltaStage: ({ direction }) => {
      const {
        protocolId,
        stageIndex: oldStageIndex,
      } = hangingProtocolService.getState();
      debugger;
      const { protocol } = hangingProtocolService.getActiveProtocol();
      for (
        let stageIndex = oldStageIndex + direction;
        stageIndex >= 0 && stageIndex < protocol.stages.length;
        stageIndex += direction
      ) {
        if (protocol.stages[stageIndex].status !== 'disabled') {
          return actions.setHangingProtocol({
            protocolId,
            stageIndex,
          });
        }
      }
      uiNotificationService.show({
        title: 'Change Stage',
        message: 'The hanging protocol has no more applicable stages',
        type: 'error',
        duration: 3000,
      });
    },

    // Ação: Anterior/Próxima Série
    thumbnailHandler: parm => {
      const { activeViewportIndex, viewports } = viewportGridService.getState();
      const displaySets = displaySetService.activeDisplaySets;
      displaySets.sort((a, b) => a.SeriesNumber - b.SeriesNumber);
      const activeIndex = displaySets.map(object => object.displaySetInstanceUID).indexOf(viewports[0].displaySetInstanceUIDs[0]);
      let updatedViewports = [];
      const viewportIndex = activeIndex + parm.index;
      if (viewportIndex >= displaySets.length || viewportIndex === -1)
        return;
      try {
        updatedViewports = hangingProtocolService.getViewportsRequireUpdate(
          activeViewportIndex,
          displaySets[viewportIndex].displaySetInstanceUID
        );
      } catch (error) {
        console.warn(error);
        uiNotificationService.show({
          title: 'Thumbnail',
          message:
            'Os conjuntos de exibição selecionados não puderam ser adicionados à janela de visualização devido a uma incompatibilidade nas regras do protocolo de suspensão.',
          type: 'info',
          duration: 3000,
        });
      }
      viewportGridService.setDisplaySetsForViewports(updatedViewports);
    },

    /**
     * Changes the viewport grid layout in terms of the MxN layout.
     */
    setViewportGridLayout: ({ numRows, numCols }) => {
      const { protocol } = hangingProtocolService.getActiveProtocol();
      const onLayoutChange = protocol.callbacks?.onLayoutChange;
      if (commandsManager.run(onLayoutChange, { numRows, numCols }) === false) {
        console.log(
          'setViewportGridLayout running',
          onLayoutChange,
          numRows,
          numCols
        );
        // Don't apply the layout if the run command returns false
        return;
      }

      const completeLayout = () => {
        const state = viewportGridService.getState();
        const stateReduce = findViewportsByPosition(
          state,
          { numRows, numCols },
          stateSyncService
        );
        const findOrCreateViewport = layoutFindOrCreate.bind(
          null,
          hangingProtocolService,
          stateReduce.viewportsByPosition
        );

        viewportGridService.setLayout({
          numRows,
          numCols,
          findOrCreateViewport,
        });
        stateSyncService.store(stateReduce);
      };
      // Need to finish any work in the callback
      window.setTimeout(completeLayout, 0);
    },

    openDICOMTagViewer() {
      const { activeViewportIndex, viewports } = viewportGridService.getState();
      const activeViewportSpecificData = viewports[activeViewportIndex];
      const { displaySetInstanceUIDs } = activeViewportSpecificData;

      const displaySets = displaySetService.activeDisplaySets;
      const { UIModalService } = servicesManager.services;

      const displaySetInstanceUID = displaySetInstanceUIDs[0];
      UIModalService.show({
        content: DicomTagBrowser,
        contentProps: {
          displaySets,
          displaySetInstanceUID,
          onClose: UIModalService.hide,
        },
        title: 'DICOM Tag Browser',
        maximizable: true
      });
    },

    /**
     * Toggle viewport overlay (the information panel shown on the four corners
     * of the viewport)
     * @see ViewportOverlay and CustomizableViewportOverlay components
     */
    toggleOverlays: () => {
      const overlays = document.getElementsByClassName('viewport-overlay');
      for (let i = 0; i < overlays.length; i++) {
        overlays.item(i).classList.toggle('hidden');
      }
    },

    toggleImageOverlay: () => {
      const overlays = document.getElementsByClassName('svg-layer');
      for (let i = 0; i < overlays.length; i++) {
        overlays.item(i).classList.toggle('hidden');
      }
    },

    abrirAslViewer: () => {
      try {
        const studies = displaySetService.activeDisplaySets;
        let aslviewerUrl = window.config.aslviewerUri?.replace('{STUDYUID}', studies[0].StudyInstanceUID);
        window.open(aslviewerUrl);
      }
      catch (ex) {
        alert('Não foi possível abrir o Asl Viewer.');
        console.log(ex);
      }
    },
    downloadDicomImagesZip: () => {
      try {
        const studies = displaySetService.activeDisplaySets;
        const urlZip = `${window.config.dataSources[0].configuration.wadoRoot}/studies/${studies[0].StudyInstanceUID}.zip`;
        window.location.href = urlZip;
      } catch (error) {
        alert('Não foi possível baixar imagens');
        console.log(error);
      }
    },
    gerarImagemSecundaria: () => {
      try {
        const { activeViewportIndex } = viewportGridService.getState();
        const { uiModalService } = servicesManager.services;

        if (uiModalService) {
          uiModalService.show({
            content: ImagemSecundariaForm,
            title: 'Gerar Imagem Secundária',
            maximizable: false,
            contentProps: {
              activeViewportIndex,
              onClose: uiModalService.hide,
              cornerstoneViewportService,
            },
          });
        }

        /*/
        const elCornerstoneViewportElement = document.getElementsByClassName('cornerstone-viewport-element');
        let imagemSecundariaDTO = [];
        for (let index = 0; index < elCornerstoneViewportElement.length; index++) {
          const elViewportElement = elCornerstoneViewportElement[index];
          const instanceUid = cornerstone.getEnabledElements()[0].viewport.csImage.imageId
            .split('instances/')[1]
            .split('/frames')[0];
          html2canvas(elViewportElement).then(canvas => {
            const img = canvas.toDataURL('image/png', 1.0);
            imagemSecundariaDTO.push({
              InstanceUid: instanceUid,
              Image: img
            });
            if (elCornerstoneViewportElement.length === (index + 1)) {
              imagemSecundariaDTO.forEach(objImagem => {
                const imagemService = new ImagemService();
                imagemService.postImagemSecundaria(objImagem)
                  .then(resp => {
                    if (resp.status === 200)
                      window.location.reload();
                  })
                  .catch(error => {
                    console.log(error);
                    alert('Falha ao gerar imagem secundária.');
                  })
              });
            }
          });
        }
        /*/
      }
      catch (error) {
        console.log(error);
      }
    },

    addSegmentation: (segmentationDisplayToolToolName = 'RectangleScissorTool') => {
      const toolGroupId = 'default';
      // const toolGroup = cornerstoneTools.ToolGroupManager.getToolGroup(toolGroupId);
      // toolGroup.addTool(segmentationDisplayToolToolName);
      // toolGroup.setToolEnabled(segmentationDisplayToolToolName);

      segmentation.segmentation.state.addSegmentation(toolGroupId, [
        {
          representationInputArray: ['segmentation1'],
          type: Enums.SegmentationRepresentations.Labelmap,
        },
      ]);
    }

  };

  const definitions = {
    showContextMenu: {
      commandFn: actions.showContextMenu,
    },
    closeContextMenu: {
      commandFn: actions.closeContextMenu,
    },
    clearMeasurements: {
      commandFn: actions.clearMeasurements,
      storeContexts: [],
      options: {},
    },
    displayNotification: {
      commandFn: actions.displayNotification,
      storeContexts: [],
      options: {},
    },
    setHangingProtocol: {
      commandFn: actions.setHangingProtocol,
      storeContexts: [],
      options: {},
    },
    toggleHangingProtocol: {
      commandFn: actions.toggleHangingProtocol,
      storeContexts: [],
      options: {},
    },
    nextStage: {
      commandFn: actions.deltaStage,
      storeContexts: [],
      options: { direction: 1 },
    },
    previousStage: {
      commandFn: actions.deltaStage,
      storeContexts: [],
      options: { direction: -1 },
    },
    setViewportGridLayout: {
      commandFn: actions.setViewportGridLayout,
      storeContexts: [],
      options: {},
    },
    openDICOMTagViewer: {
      commandFn: actions.openDICOMTagViewer,
    },
    addSegmentation: {
      commandFn: actions.addSegmentation
    },
    abrirAslViewer: {
      commandFn: actions.abrirAslViewer
    },
    downloadDicomImagesZip: {
      commandFn: actions.downloadDicomImagesZip
    },
    gerarImagemSecundaria: {
      commandFn: actions.gerarImagemSecundaria
    },
    thumbnailHandler: {
      commandFn: actions.thumbnailHandler,
      options: {},
    },
    toggleOverlays: {
      commandFn: actions.toggleOverlays
    },
    toggleImageOverlay: {
      commandFn: actions.toggleImageOverlay
    },
  };

  return {
    actions,
    definitions,
    defaultContext: 'DEFAULT',
  };
};

export default commandsModule;
